import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Switch } from '@heroui/react';
import { useCreateForwarder, useUpdateForwarder } from '@/features/forwarders/hooks';
import { forwarderSchema, type ForwarderFormValues } from '@/features/forwarders/schema';
import type { Forwarder } from '@/features/forwarders/types';
import { XMarkIcon, PlusIcon } from '@/icons';

export function ForwarderFormModal({ isOpen, onClose, forwarder }: { isOpen: boolean; onClose: () => void; forwarder?: Forwarder | null }) {
  const isEdit = !!forwarder;
  const create = useCreateForwarder();
  const update = useUpdateForwarder();
  const [destInput, setDestInput] = useState('');
  const form = useForm<ForwarderFormValues>({ resolver: zodResolver(forwarderSchema), defaultValues: { address: '', destinations: [], keepCopy: false, enabled: true } });

  useEffect(() => {
    if (forwarder) form.reset({ address: forwarder.address, destinations: forwarder.destinations, keepCopy: forwarder.keepCopy, enabled: forwarder.enabled });
    else form.reset({ address: '', destinations: [], keepCopy: false, enabled: true });
    setDestInput('');
  }, [forwarder]); // eslint-disable-line

  const onSubmit = form.handleSubmit(async (v) => {
    if (isEdit) await update.mutateAsync({ id: forwarder!.id, body: v });
    else await create.mutateAsync(v);
    onClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalContent>
        <ModalHeader className="text-fg">{isEdit ? 'Edit forwarder' : 'Create forwarder'}</ModalHeader>
        <ModalBody>
          <form id="fwd-form" onSubmit={onSubmit} className="space-y-4" noValidate>
            <Input {...form.register('address')} label="Source address" placeholder="team@acme.com" variant="bordered" autoFocus isReadOnly={isEdit}
              isInvalid={!!form.formState.errors.address} errorMessage={form.formState.errors.address?.message} />
            <Controller control={form.control} name="destinations" render={({ field }) => (
              <div>
                <label className="mb-1 block text-sm text-fg-muted">Destinations</label>
                <div className="flex gap-2">
                  <Input value={destInput} onValueChange={setDestInput} placeholder="add address and press +" variant="bordered" size="sm"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (destInput) { field.onChange([...field.value, destInput.trim()]); setDestInput(''); } } }} />
                  <Button isIconOnly size="sm" variant="flat" aria-label="Add destination"
                    onPress={() => { if (destInput) { field.onChange([...field.value, destInput.trim()]); setDestInput(''); } }}>
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                </div>
                {field.value.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {field.value.map((d, i) => (
                      <span key={`${d}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-surface-raised px-2 py-1 text-xs text-fg">
                        {d}
                        <button type="button" onClick={() => field.onChange(field.value.filter((_, j) => j !== i))} aria-label={`Remove ${d}`}><XMarkIcon className="h-3 w-3 text-fg-subtle hover:text-fg" /></button>
                      </span>
                    ))}
                  </div>
                )}
                {form.formState.errors.destinations && <p className="mt-1 text-xs text-danger">{form.formState.errors.destinations.message}</p>}
              </div>
            )} />
            <Controller control={form.control} name="keepCopy" render={({ field }) => (
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div><p className="text-sm font-medium text-fg">Keep a copy</p><p className="text-xs text-fg-muted">Also deliver to the source mailbox.</p></div>
                <Switch isSelected={field.value} onValueChange={field.onChange} aria-label="Keep a copy" />
              </div>
            )} />
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button color="primary" type="submit" form="fwd-form" isLoading={form.formState.isSubmitting}>{isEdit ? 'Save' : 'Create'}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
